import { Type } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDateString,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
  ValidateNested,
} from 'class-validator';
import { BaseDto } from '../base.dto';
import { TeleDifferentialDiagnosisMutationDto } from '../diagnoses';
import { MedicalUnitDto } from '../medical-unit.dtos';
import { UserDto } from '../user.dto';
import { ClinicalManagementActionDto } from './clinical-management-action.dto';
import { ImageQualityRatingDto } from './image-quality-rating.dto';
import { UrgencyRatingDto } from './urgency-rating.dto';
import { TeledermatologicalAssessmentRatingDto } from './teledermatological-assessment-rating.dtos';
import { ReferralDto } from './referral.dtos';
import type { TeleResponseReviewDto } from './tele-response-review.dtos';

export class CreateTeledermatologicalAssessmentDto {
  @IsNotEmpty()
  @IsString()
  @IsUUID()
  referralId: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  diagnosisId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  difficultyId?: string;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => TeleDifferentialDiagnosisMutationDto)
  differentialDiagnoses?: TeleDifferentialDiagnosisMutationDto[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  clinicalImageQualityRatingId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString({ each: true })
  clinicalImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  clinicalImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  dermoscopicImageQualityRatingId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString({ each: true })
  dermoscopicImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  dermoscopicImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @IsNotEmpty()
  closeUpImageQualityRatingId?: string;

  @IsOptional()
  @IsNotEmpty()
  closeUpImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  closeUpImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  proposedClinicalManagementActionId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  proposedClinicalManagementActionDescription?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  urgencyRatingId?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  note?: string;

  @IsOptional()
  @IsNotEmpty()
  @IsNumber()
  @Min(0)
  timeSpentReviewing?: number;
}

export class TeledermatologicalAssessmentDto implements BaseDto {
  @IsString()
  id: string;
  createdByUser?: UserDto;
  updatedByUser?: UserDto;
  deletedByUser?: UserDto;
  created?: string;
  updated?: string;
  deleted?: string;

  @IsNotEmpty()
  @IsString()
  referralId: string;

  @IsOptional()
  @ValidateNested()
  referral?: ReferralDto;

  @IsNotEmpty()
  @IsString()
  diagnosisId: string;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  difficultyId?: string;

  @IsOptional()
  @ValidateNested()
  differentialDiagnoses?: TeleDifferentialDiagnosisMutationDto[];

  @IsOptional()
  @ValidateNested()
  clinicalImageQualityRating?: ImageQualityRatingDto;

  @IsOptional()
  @IsNotEmpty()
  @IsString({ each: true })
  clinicalImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  clinicalImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @ValidateNested()
  closeUpImageQualityRating?: ImageQualityRatingDto;

  @IsOptional()
  @IsNotEmpty()
  @IsString({ each: true })
  closeUpImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  closeUpImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @ValidateNested()
  dermoscopicImageQualityRating?: ImageQualityRatingDto;

  @IsOptional()
  @IsNotEmpty()
  @IsString({ each: true })
  dermoscopicImageQualityRatingReasonsIds?: string[];

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  dermoscopicImageQualityRatingReasonsDescription?: string;

  @IsOptional()
  @ValidateNested()
  proposedClinicalManagementAction?: ClinicalManagementActionDto;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  proposedClinicalManagementActionDescription?: string;

  @IsOptional()
  @ValidateNested()
  urgencyRating?: UrgencyRatingDto;

  @IsOptional()
  @IsNotEmpty()
  @IsString()
  note?: string;

  @IsNotEmpty()
  @IsString()
  createdByUserName: string;

  @IsNotEmpty()
  @IsString()
  medicalUnitId?: string;

  @ValidateNested()
  medicalUnit?: MedicalUnitDto;

  @IsOptional()
  @IsBoolean()
  canEdit?: boolean;

  @IsOptional()
  @ValidateNested()
  rating?: TeledermatologicalAssessmentRatingDto;

  @IsOptional()
  @IsNotEmpty()
  @IsNumber()
  timeSpentReviewing?: number;

  @IsOptional()
  @ValidateNested()
  pcpReview?: TeleResponseReviewDto;
}

export class SearchTeledermatologicalAssessmentsDto {
  @IsOptional()
  @IsUUID()
  imageCreatedByUserId?: string;

  @IsOptional()
  @IsBoolean()
  filterByImmersionFluidRelatedReasons?: boolean;

  @IsOptional()
  @IsDateString()
  createdAfter?: string;

  @IsOptional()
  @IsNumber()
  @Max(50)
  take?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;
}
